import React from "react";
import { Avatar } from "antd";
import { convertToInitials } from "../layoutComponent/layoutHelpers";

/**
 * Standard 24x24 avatar used across tables, drawers, and row identities.
 * Renders the profile image when `src` is provided and falls back to
 * initials derived from `name`. All call sites share the same size and
 * tint so identity cells feel uniform end-to-end.
 *
 * @param {string} [name] - Used to derive initials and as `alt` text.
 * @param {string} [src] - Image URL.
 * @param {string|number} [size="small"] - AntD Avatar size token.
 * @param {string} [bgClass="bg-twLinkColor"] - Background tint class. Override
 *   when a surface needs a different color (e.g. dashboard tables use
 *   `bg-grey-700`).
 * @param {string} [className] - Extra classes appended after the defaults.
 * @param {object} [rest] - Forwarded to the underlying AntD Avatar.
 */
// AntD v5 default primary (#1677FF) — matches the project's `twLinkColor` so
// avatars pick up the same brand blue as primary buttons and links by default.
// Callers can override via `bgClass` when a surface needs a different tint
// (e.g. the engagement dashboard's slate-grey table rows).
const DEFAULT_BG_CLASS = "bg-twLinkColor";
const DEFAULT_SIZE = 24;

// `flex-shrink-0` keeps the avatar from being squished into an ellipse when
// it sits inside a tight flex row. The `custom-avatar` class is the hook for
// the global override in main.scss that disables AntD's per-text auto-scaling
// so initials render at a consistent size regardless of letter width.
const CustomAvatar = ({
  name,
  src,
  size = DEFAULT_SIZE,
  bgClass = DEFAULT_BG_CLASS,
  className = "",
  ...rest
}) => {
  const initials = convertToInitials(name);
  // Pin width/height/line-height inline so neighbour content (a long, wrapped
  // username next to us) can never alter the rendered avatar diameter.
  const dimension = typeof size === "number" ? `${size}px` : undefined;
  const fixedSizeStyle = dimension
    ? { width: dimension, height: dimension, minWidth: dimension, lineHeight: dimension }
    : undefined;
  return (
    <Avatar
      size={size}
      src={src || undefined}
      alt={name || undefined}
      style={fixedSizeStyle}
      className={`flex-shrink-0 custom-avatar ${bgClass}${className ? ` ${className}` : ""}`}
      {...rest}
    >
      {initials}
    </Avatar>
  );
};

export default CustomAvatar;
